import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ValidationSummary, ImportOptions } from '../../../types/import.types';
import { importService } from '../../../services/import.service';

interface ReviewStepProps {
  jobId: string;
  summary: any;
  options: ImportOptions;
  onComplete: () => void;
  onBack: () => void;
  onOptionsChange: (options: ImportOptions) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  jobId,
  summary,
  options,
  onComplete,
  onBack,
  onOptionsChange,
}) => {
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update' | 'create'>('skip');
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStartImport = async () => {
    setIsStarting(true);
    setError(null);
    
    try {
      // Call the approve API to start the actual import
      await importService.approveImport(
        jobId,
        duplicateHandling,
        true // includeWarnings
      );
      
      // Now move to progress step
      onComplete();
    } catch (err: any) {
      console.error('Error starting import:', err);
      setError(err.response?.data?.message || err.message || 'Failed to start import');
      setIsStarting(false);
    }
  };

  const totalToImport = summary.valid + summary.warnings;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review and Start Import
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Review the import summary and choose how to handle duplicates.
      </Typography>

      {/* Import Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Import Summary
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Total Records:</strong> {summary.total}
          </Typography>
          <Typography variant="body2" color="success.main" gutterBottom>
            <strong>Will be Imported:</strong> {totalToImport} ({summary.valid} valid + {summary.warnings} with warnings)
          </Typography>
          {summary.errors > 0 && (
            <Typography variant="body2" color="error.main" gutterBottom>
              <strong>Will be Skipped:</strong> {summary.errors} (errors)
            </Typography>
          )}
          {summary.duplicates > 0 && (
            <Typography variant="body2" color="info.main" gutterBottom>
              <strong>Duplicates Found:</strong> {summary.duplicates}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" gutterBottom>
          <strong>Default Status:</strong> {options.defaultStatus}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Default Priority:</strong> {options.defaultPriority}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Assign to Me:</strong> {options.assignToCreator ? 'Yes' : 'No'}
        </Typography>
      </Paper>

      {/* Duplicate Handling */}
      {summary.duplicates > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Duplicate Handling
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            We found {summary.duplicates} duplicate record(s). Choose how to handle them:
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Duplicate Handling</InputLabel>
            <Select
              value={duplicateHandling}
              label="Duplicate Handling"
              onChange={(e) => setDuplicateHandling(e.target.value as 'skip' | 'update' | 'create')}
            >
              <MenuItem value="skip">
                Skip duplicates (recommended) - Don't import duplicate records
              </MenuItem>
              <MenuItem value="update">
                Update existing - Update existing leads with new data
              </MenuItem>
              <MenuItem value="create">
                Create anyway - Import as new leads (will create duplicates)
              </MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Warnings */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What happens next:
        </Typography>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>{totalToImport} leads will be imported to your database</li>
          {summary.errors > 0 && (
            <li>{summary.errors} records with errors will be skipped</li>
          )}
          {summary.duplicates > 0 && (
            <li>Duplicates will be handled according to your selection</li>
          )}
          <li>You'll see a progress indicator during the import</li>
          <li>You can view the results when complete</li>
        </ul>
      </Alert>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack} variant="outlined" disabled={isStarting}>
          Back
        </Button>
        <Button
          onClick={handleStartImport}
          variant="contained"
          color="primary"
          size="large"
          disabled={isStarting}
          startIcon={isStarting ? <CircularProgress size={20} /> : null}
        >
          {isStarting ? 'Starting Import...' : `Start Import (${totalToImport} leads)`}
        </Button>
      </Box>
    </Box>
  );
};